// src/AddStudent.js
import React, { useState, useEffect, useMemo } from 'react';
import API from './api';
import { toast } from 'react-toastify';
import { FaUserPlus, FaSave, FaTimes, FaEdit } from 'react-icons/fa';

import StudentTaxonomyEditor from './StudentTaxonomyEditor';
import { saveStudentTaxonomy } from './api';
import SuggestMatchesPanel from './SuggestMatchesPanel';

const today = new Date().toISOString().split('T')[0];
const emptyParent = { id: undefined, first_name: '', last_name: '', phone: '', email: '', relation: 'mother' };

const requiredFields = [
  'first_name', 'last_name', 'dob', 'gender', 'default_teacher_id',
  'address_street', 'address_city', 'address_state', 'address_zip', 'address_country',
  'uci'
];

const teacherHasProgram = (teacherTaxo, programCode) => {
  if (!teacherTaxo || !Array.isArray(teacherTaxo.strengths)) return false;
  return teacherTaxo.strengths.some(s => (s.code || '').toUpperCase() === programCode.toUpperCase());
};

const PROGRAM_EI = 'PROG_EI';
const PROGRAM_PI = 'PROG_PI';

const AddStudent = ({ student, onSaved, onCancel }) => {
  const [needs, setNeeds] = useState({ needs: [] });
  const [needsDirty, setNeedsDirty] = useState(false);

  const [teachers, setTeachers] = useState([]);
  const [teacherTaxoMap, setTeacherTaxoMap] = useState(new Map()); // id -> { strengths, limitations }
  const [taxoLoaded, setTaxoLoaded] = useState(false);             // NEW: gate za validaciju
  const [loading, setLoading] = useState(false);

  const [studentData, setStudentData] = useState({
    first_name: '', last_name: '', dob: '', gender: '',
    is_active: true, default_teacher_id: '',
    latitude: '', longitude: '', notes: '', uci: '',
    program_type: 'EI',
    address_street: '', address_city: '', address_state: '', address_zip: '', address_country: '',
    alt_address_street: '', alt_address_city: '', alt_address_state: '', alt_address_zip: '', alt_address_country: '',
    service_start_date: today, service_end_date: today,
    intake_goals: '', additional_notes: '', parent_concerns: '',
    referring_agent: '', referring_agent_phone: '', referring_agent_unit: '', referring_agent_unit_id: '',
    onboarding_documents: 'not signed',
    provider_gender_preference: 'any',
    telehealth_ok: false,
    parents: [{ ...emptyParent }]
  });

  const [errors, setErrors] = useState({});
  const [teacherHistory, setTeacherHistory] = useState([]);

  const isEditMode = !!(student && student.id);

  // Load student taxonomy (edit)
  useEffect(() => {
    if (student?.id) {
      API.get(`/students/${student.id}/taxonomy`)
        .then(res => setNeeds({ needs: res.data?.needs || [] }))
        .catch(() => setNeeds({ needs: [] }));
    } else {
      setNeeds({ needs: [] });
    }
    setNeedsDirty(false);
  }, [student]);

  // Load teachers + their taxonomy
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get('/teachers');
        const base = (res.data || []).filter(t => t.is_active);
        if (cancelled) return;
        setTeachers(base);

        const entries = await Promise.all(
          base.map(async (t) => {
            try {
              const tx = await API.get(`/teachers/${t.id}/taxonomy`);
              return [t.id, tx.data || { strengths: [], limitations: [] }];
            } catch {
              return [t.id, { strengths: [], limitations: [] }];
            }
          })
        );
        if (cancelled) return;
        setTeacherTaxoMap(new Map(entries));
        setTaxoLoaded(true); // NEW
      } catch (err) {
        if (cancelled) return;
        setTeachers([]);
        setTeacherTaxoMap(new Map());
        setTaxoLoaded(true); // čak i u grešci – da ne blokira formu
        toast.error('Error loading teachers');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Populate student for edit
  useEffect(() => {
    if (student) {
      setStudentData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        dob: student.dob?.slice(0, 10) || '',
        gender: student.gender || '',
        is_active: student.is_active ?? true,
        default_teacher_id: student.default_teacher_id || '',
        latitude: student.latitude || '',
        longitude: student.longitude || '',
        notes: student.notes || '',
        uci: student.uci || '',
        program_type: student.program_type || 'EI',
        address_street: student.address_street || '',
        address_city: student.address_city || '',
        address_state: student.address_state || '',
        address_zip: student.address_zip || '',
        address_country: student.address_country || '',
        alt_address_street: student.alt_address_street || '',
        alt_address_city: student.alt_address_city || '',
        alt_address_state: student.alt_address_state || '',
        alt_address_zip: student.alt_address_zip || '',
        alt_address_country: student.alt_address_country || '',
        service_start_date: student.service_start_date?.slice(0, 10) || today,
        service_end_date: student.service_end_date?.slice(0, 10) || today,
        intake_goals: student.intake_goals || '',
        additional_notes: student.additional_notes || '',
        parent_concerns: student.parent_concerns || '',
        referring_agent: student.referring_agent || '',
        referring_agent_phone: student.referring_agent_phone || '',
        referring_agent_unit: student.referring_agent_unit || '',
        referring_agent_unit_id: student.referring_agent_unit_id || '',
        onboarding_documents: student.onboarding_documents || 'not signed',
        provider_gender_preference: student.provider_gender_preference || 'any',
        telehealth_ok: !!student.telehealth_ok,
        parents: student.parents?.length
          ? student.parents.map(p => ({
              id: p.id || undefined,
              first_name: p.first_name || '',
              last_name: p.last_name || '',
              phone: p.phone || '',
              email: p.email || '',
              relation: p.relation || 'guardian'
            }))
          : [{ ...emptyParent }]
      });

      API.get(`/students/${student.id}/teacher-history`)
        .then(res => setTeacherHistory(res.data || []))
        .catch(() => setTeacherHistory([]));
    } else {
      setTeacherHistory([]);
    }
    setNeedsDirty(false);
  }, [student]);

  // Helper
  const isTeacherQualifiedForProgram = (teacherId, programType) => {
    const tx = teacherTaxoMap.get(Number(teacherId));
    if (!tx) return false;
    if (programType === 'PI') return teacherHasProgram(tx, PROGRAM_PI);
    return teacherHasProgram(tx, PROGRAM_EI);
  };

  // IMPORTANT: ne resetuj default_teacher_id dok taksonomija NIJE učitana
  useEffect(() => {
    if (!taxoLoaded) return;
    const tid = studentData.default_teacher_id;
    if (!tid) return;
    const ok = isTeacherQualifiedForProgram(tid, studentData.program_type);
    if (!ok) {
      // Samo ako SIGURNO znamo da nije kvalifikovan (posle load-a)
      setStudentData(prev => ({ ...prev, default_teacher_id: '' }));
      toast.info('Selected teacher is not qualified for the chosen program. Please select another teacher.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData.program_type, taxoLoaded]);

  // Filter kvalifikovanih
  const qualifiedTeachers = useMemo(() => {
    const p = studentData.program_type || 'EI';
    if (!taxoLoaded) return []; // dok se ne učita – ne nudimo listu, sprečava flicker
    return teachers.filter(t => isTeacherQualifiedForProgram(t.id, p));
  }, [teachers, teacherTaxoMap, studentData.program_type, taxoLoaded]);

  // Ako smo u EDIT-u i current teacher nije kvalifikovan, i dalje ga prikaži na vrhu (ali ga ne guraj kad taxoLoaded=false)
  const teacherOptions = useMemo(() => {
    const opts = [...qualifiedTeachers];
    const currentId = Number(studentData.default_teacher_id);
    if (taxoLoaded && currentId && !opts.some(t => t.id === currentId)) {
      const found = teachers.find(t => t.id === currentId);
      if (found) opts.unshift(found);
    }
    return opts;
  }, [qualifiedTeachers, teachers, studentData.default_teacher_id, taxoLoaded]);

  const validate = () => {
    let errs = {};
    requiredFields.forEach(f => {
      if (!studentData[f] || (typeof studentData[f] === 'string' && !studentData[f].trim())) {
        errs[f] = 'Required';
      }
    });

    // Validacija programa – radi samo kada taxoLoaded
    if (taxoLoaded && studentData.default_teacher_id) {
      const ok = isTeacherQualifiedForProgram(studentData.default_teacher_id, studentData.program_type);
      if (!ok) errs.default_teacher_id = 'Selected teacher is not qualified for the chosen program';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStudentChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'provider_gender_preference' || name === 'telehealth_ok') {
      setNeedsDirty(true);
    }
    setStudentData(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : name === 'default_teacher_id'
          ? (value === '' ? null : parseInt(value))
          : value
    }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleParentChange = (index, e) => {
    const { name, value } = e.target;
    setStudentData(prev => {
      const updated = [...prev.parents];
      updated[index] = { ...updated[index], [name]: value };
      return { ...prev, parents: updated };
    });
  };

  const addParent = () => setStudentData(prev => ({
    ...prev,
    parents: [...prev.parents, { ...emptyParent, relation: 'guardian' }]
  }));
  const removeParent = (index) => setStudentData(prev => ({
    ...prev,
    parents: prev.parents.filter((_, i) => i !== index)
  }));

  const saveAll = async (stayOpen = false) => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...studentData,
        default_teacher_id: isNaN(studentData.default_teacher_id) ? null : Number(studentData.default_teacher_id),
        latitude: studentData.latitude || null,
        longitude: studentData.longitude || null,
        address_country: studentData.address_country || '',
        service_start_date: studentData.service_start_date || today,
        service_end_date: studentData.service_end_date || today
      };

      let studentId;
      const isNew = !student?.id;

      if (!isNew) {
        const res = await API.put(`/students/${student.id}`, payload);
        studentId = student.id;
        toast.success('Student updated!');
        if (res.data?.teacherChanged) {
          toast.info('Be sure to update scheduled visits for this student, as all existing visits have been automatically marked as rejected.');
        }
        API.get(`/students/${student.id}/teacher-history`)
          .then(res => setTeacherHistory(res.data || []));
      } else {
        const res = await API.post('/students', payload);
        studentId = res.data?.student_id || res.data?.id;
        toast.success('Student saved!');
        setTimeout(() => {
          toast.info('You can now set Needs & Preferences and view Suggested Teachers from the Edit Student screen.', { autoClose: 6000 });
        }, 300);
      }

      if (studentId) {
        const needsPayload = (needs?.needs || []).map(x =>
          typeof x === 'string'
            ? { code: x, priority: 1, required: false }
            : { code: x.code, priority: x.priority || 1, required: !!x.required }
        );
        try { await saveStudentTaxonomy(studentId, { needs: needsPayload }); } catch (_) {}
      }

      if (stayOpen) {
        toast.info('Tip: Suggested Teachers may change with your updates. Use Refresh in that panel if needed.');
        setNeedsDirty(false);
        return;
      }

      onSaved && onSaved();

      if (!student?.id) {
        // reset forme posle ADD
        setStudentData({
          first_name: '', last_name: '', dob: '', gender: '',
          is_active: true, default_teacher_id: '',
          latitude: '', longitude: '', notes: '', uci: '',
          program_type: 'EI',
          address_street: '', address_city: '', address_state: '', address_zip: '', address_country: '',
          alt_address_street: '', alt_address_city: '', alt_address_state: '', alt_address_zip: '', alt_address_country: '',
          service_start_date: today, service_end_date: today,
          intake_goals: '', additional_notes: '', parent_concerns: '',
          referring_agent: '', referring_agent_phone: '', referring_agent_unit: '', referring_agent_unit_id: '',
          onboarding_documents: 'not signed',
          provider_gender_preference: 'any',
          telehealth_ok: false,
          parents: [{ ...emptyParent }]
        });
        setNeeds({ needs: [] });
        setNeedsDirty(false);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      if (msg === 'UCI must be unique') {
        toast.error('UCI must be unique. Another student already has this UCI.');
        setErrors(prev => ({ ...prev, uci: 'Duplicate UCI' }));
      } else if (err.response?.data?.error === 'Address not found. Please check.') {
        toast.error('Address not found! Please check all address fields and try again.');
        setErrors(prev => ({
          ...prev,
          address_street: 'Invalid',
          address_city: 'Invalid',
          address_state: 'Invalid',
          address_zip: 'Invalid',
          address_country: 'Invalid'
        }));
      } else if (err.response?.data?.missing) {
        let errs = {};
        for (let f of err.response.data.missing) errs[f] = 'Required';
        setErrors(errs);
        toast.error('Please fill all required fields.');
      } else {
        toast.error(msg || 'Failed to save student');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveAll(false);
  };

  const renderFieldError = field => errors[field] && (
    <span className="text-red-500 text-xs ml-1">{errors[field]}</span>
  );

  const currentTeacherNotQualified = (() => {
    if (!taxoLoaded) return false;
    const tid = Number(studentData.default_teacher_id);
    if (!tid) return false;
    return !isTeacherQualifiedForProgram(tid, studentData.program_type);
  })();

  return (
    <form onSubmit={handleSubmit} className="space-y-3 bg-white p-4 rounded shadow max-w-2xl mx-auto">
      <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
        {student ? <FaEdit /> : <FaUserPlus />} {student ? 'Edit' : 'Add'} Student
      </h2>

      {/* Basic */}
      <div className="grid md:grid-cols-2 gap-2">
        <div>
          <input name="first_name" value={studentData.first_name} onChange={handleStudentChange}
            placeholder="First Name *" className={`w-full border p-2 rounded ${errors.first_name ? 'border-red-500' : ''}`} />
          {renderFieldError('first_name')}
        </div>
        <div>
          <input name="last_name" value={studentData.last_name} onChange={handleStudentChange}
            placeholder="Last Name *" className={`w-full border p-2 rounded ${errors.last_name ? 'border-red-500' : ''}`} />
          {renderFieldError('last_name')}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-2">
        <div>
          <input type="date" name="dob" value={studentData.dob} onChange={handleStudentChange}
            className={`w-full border p-2 rounded ${errors.dob ? 'border-red-500' : ''}`} />
          {renderFieldError('dob')}
        </div>
        <div>
          <select name="gender" value={studentData.gender} onChange={handleStudentChange}
            className={`w-full border p-2 rounded ${errors.gender ? 'border-red-500' : ''}`}>
            <option value="">Gender *</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          {renderFieldError('gender')}
        </div>
        <div className="flex items-center mt-1 md:mt-0">
          <input type="checkbox" name="is_active" checked={studentData.is_active}
            onChange={handleStudentChange} className="mr-2" />
          <label>Active</label>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-2">
        {/* Teacher (filtered by program) */}
        <div>
          <select
            name="default_teacher_id"
            value={studentData.default_teacher_id || ''}
            onChange={handleStudentChange}
            className={`w-full border p-2 rounded ${errors.default_teacher_id ? 'border-red-500' : ''}`}
            title="Select Teacher (filtered by program)"
            disabled={!taxoLoaded}
          >
            <option value="">{taxoLoaded ? 'Select Teacher *' : 'Loading teachers...'}</option>

            {(() => {
              const curId = Number(studentData.default_teacher_id);
              const curTeacher = teachers.find(t => t.id === curId);
              if (taxoLoaded && curTeacher && currentTeacherNotQualified) {
                return (
                  <option key={`cur-${curId}`} value={curId}>
                    {curTeacher.name} — NOT qualified for {studentData.program_type}
                  </option>
                );
              }
              return null;
            })()}

            {teacherOptions
              .filter(t => !taxoLoaded || isTeacherQualifiedForProgram(t.id, studentData.program_type))
              .map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
          {renderFieldError('default_teacher_id')}
          {taxoLoaded && currentTeacherNotQualified && (
            <div className="text-xs text-red-600 mt-1">
              Selected teacher is not qualified for the chosen program — please re-select.
            </div>
          )}
        </div>

        {/* Program type (dropdown pored teacher-a, nema ga u Needs & Preferences) */}
        <div>
          <select
            name="program_type"
            value={studentData.program_type}
            onChange={handleStudentChange}
            className="w-full border p-2 rounded"
            title="Program type"
          >
            <option value="EI">EI (Early Intervention)</option>
            <option value="PI">PI (Project Impact)</option>
          </select>
        </div>
      </div>

      {/* Teacher history */}
      {isEditMode && (
        <div className="mt-2">
          <h4 className="font-semibold text-xs mb-1">Teacher History</h4>
          <ul className="bg-gray-50 border rounded p-2 text-xs max-h-32 overflow-y-auto">
            {teacherHistory.length === 0 ? (
              <li>No teacher changes recorded.</li>
            ) : (
              teacherHistory.map(item => (
                <li key={item.id} className="mb-1">
                  <span className="font-semibold">{item.old_teacher_name || 'Unknown'}</span>
                  {' '}→{' '}
                  <span className="font-semibold">{item.new_teacher_name || 'Unknown'}</span>
                  <span className="ml-2 text-gray-500">({new Date(item.changed_at).toLocaleString()})</span>
                  {item.admin_name && <span className="ml-2 text-blue-700">by {item.admin_name}</span>}
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Address */}
      <h3 className="font-semibold mt-2">Primary Address *</h3>
      <div className="grid md:grid-cols-3 gap-2">
        <div>
          <input name="address_street" value={studentData.address_street} onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${errors.address_street ? 'border-red-500' : ''}`}
            placeholder="Street" />
          {renderFieldError('address_street')}
        </div>
        <div>
          <input name="address_city" value={studentData.address_city} onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${errors.address_city ? 'border-red-500' : ''}`}
            placeholder="City" />
          {renderFieldError('address_city')}
        </div>
        <div>
          <input name="address_state" value={studentData.address_state} onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${errors.address_state ? 'border-red-500' : ''}`}
            placeholder="State" />
          {renderFieldError('address_state')}
        </div>
        <div>
          <input name="address_zip" value={studentData.address_zip} onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${errors.address_zip ? 'border-red-500' : ''}`}
            placeholder="ZIP" />
          {renderFieldError('address_zip')}
        </div>
        <div>
          <input name="address_country" value={studentData.address_country} onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${errors.address_country ? 'border-red-500' : ''}`}
            placeholder="Country" />
          {renderFieldError('address_country')}
        </div>
      </div>

      {/* UCI */}
      <input name="uci" value={studentData.uci} onChange={handleStudentChange}
        className={`border p-2 rounded w-full ${errors.uci ? 'border-red-500' : ''}`} placeholder="UCI *" />
      {renderFieldError('uci')}

      <textarea name="notes" value={studentData.notes} onChange={handleStudentChange}
        className="border p-2 rounded w-full" placeholder="Notes" />

      {/* Alternative Address */}
      <h3 className="font-semibold mt-2">Alternative Address (optional)</h3>
      <div className="grid md:grid-cols-3 gap-2">
        <div>
          <input name="alt_address_street" value={studentData.alt_address_street} onChange={handleStudentChange}
            className="border p-2 rounded w-full" placeholder="Street" />
        </div>
        <div>
          <input name="alt_address_city" value={studentData.alt_address_city} onChange={handleStudentChange}
            className="border p-2 rounded w-full" placeholder="City" />
        </div>
        <div>
          <input name="alt_address_state" value={studentData.alt_address_state} onChange={handleStudentChange}
            className="border p-2 rounded w-full" placeholder="State" />
        </div>
        <div>
          <input name="alt_address_zip" value={studentData.alt_address_zip} onChange={handleStudentChange}
            className="border p-2 rounded w-full" placeholder="ZIP" />
        </div>
        <div>
          <input name="alt_address_country" value={studentData.alt_address_country} onChange={handleStudentChange}
            className="border p-2 rounded w-full" placeholder="Country" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-2">
        <input type="date" name="service_start_date" value={studentData.service_start_date} onChange={handleStudentChange}
          className="border p-2 rounded w-full" placeholder="Start of Service Date" />
        <input type="date" name="service_end_date" value={studentData.service_end_date} onChange={handleStudentChange}
          className="border p-2 rounded w-full" placeholder="End of Service Date" />
      </div>

      <textarea name="intake_goals" value={studentData.intake_goals} onChange={handleStudentChange}
        className="border p-2 rounded w-full" placeholder="Intake Development Goals" />
      <textarea name="additional_notes" value={studentData.additional_notes} onChange={handleStudentChange}
        className="border p-2 rounded w-full" placeholder="Additional Notes" />
      <textarea name="parent_concerns" value={studentData.parent_concerns} onChange={handleStudentChange}
        className="border p-2 rounded w-full" placeholder="Parent Concerns" />

      {isEditMode ? (
        <>
          <h3 className="font-semibold mt-2">Needs & Preferences</h3>

          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <label className="text-sm text-gray-700">Gender preference</label>
              <select
                name="provider_gender_preference"
                value={studentData.provider_gender_preference}
                onChange={handleStudentChange}
                className="w-full border p-2 rounded"
              >
                <option value="any">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          <StudentTaxonomyEditor
            value={needs}
            onChange={(val) => { setNeeds(val); setNeedsDirty(true); }}
          />

          <div className="mt-3">
            <SuggestMatchesPanel studentId={student.id} />
          </div>
        </>
      ) : (
        <div className="mt-2 text-xs text-green-500">
          Save the student first to set Needs & Preferences and see Suggested Teachers.
        </div>
      )}

      <h3 className="font-semibold mt-2">Referring Agent</h3>
      <div className="grid md:grid-cols-2 gap-2">
        <input name="referring_agent" value={studentData.referring_agent} onChange={handleStudentChange}
          className="border p-2 rounded" placeholder="Agent Name" />
        <input name="referring_agent_phone" value={studentData.referring_agent_phone} onChange={handleStudentChange}
          className="border p-2 rounded" placeholder="Agent Phone" />
        <input name="referring_agent_unit" value={studentData.referring_agent_unit} onChange={handleStudentChange}
          className="border p-2 rounded" placeholder="Unit" />
        <input name="referring_agent_unit_id" value={studentData.referring_agent_unit_id} onChange={handleStudentChange}
          className="border p-2 rounded" placeholder="Unit ID" />
      </div>

      <label className="mt-2 block font-semibold">Onboarding Documents</label>
      <select name="onboarding_documents" value={studentData.onboarding_documents} onChange={handleStudentChange}
        className="border p-2 rounded w-full">
        <option value="signed">Signed</option>
        <option value="not signed">Not Signed</option>
      </select>

      {/* Parents */}
      <div className="border-top pt-4">
        <h3 className="font-semibold mb-2">Parents</h3>
        {studentData.parents.map((parent, index) => (
          <div key={index} className="border p-3 mb-3 rounded bg-gray-50 space-y-2">
            <div className="flex gap-2">
              <input name="first_name" value={parent.first_name}
                onChange={e => handleParentChange(index, e)} placeholder="First Name"
                className="w-1/2 border p-2 rounded" required />
              <input name="last_name" value={parent.last_name}
                onChange={e => handleParentChange(index, e)} placeholder="Last Name"
                className="w-1/2 border p-2 rounded" />
            </div>
            <input name="phone" value={parent.phone}
              onChange={e => handleParentChange(index, e)} placeholder="Phone"
              className="w-full border p-2 rounded" />
            <input name="email" value={parent.email}
              onChange={e => handleParentChange(index, e)} placeholder="Email"
              className="w-full border p-2 rounded" />
            <select name="relation" value={parent.relation}
              onChange={e => handleParentChange(index, e)} className="w-full border p-2 rounded">
              <option value="mother">Mother</option>
              <option value="father">Father</option>
              <option value="guardian">Guardian</option>
            </select>
            {studentData.parents.length > 1 && (
              <button type="button" onClick={() => removeParent(index)}
                className="text-red-500 underline text-sm"><FaTimes className="inline mr-1" /> Remove</button>
            )}
          </div>
        ))}
        <button type="button" onClick={addParent}
          className="text-blue-600 underline text-sm"><FaUserPlus className="inline mr-1" /> Add Another Parent</button>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        {onCancel && (
          <button type="button" onClick={onCancel} className="bg-gray-300 px-4 py-2 rounded flex items-center gap-2">
            <FaTimes /> Cancel
          </button>
        )}

        {isEditMode && needsDirty ? (
          <button
            type="button"
            onClick={() => saveAll(true)}
            disabled={loading}
            className="bg-orange-600 text-white px-4 py-2 rounded flex items-center gap-2"
            title="Save updates but stay on this screen"
          >
            <FaSave /> {loading ? 'Updating...' : 'Update Student'}
          </button>
        ) : (
          <button
            type="submit"
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <FaSave /> {loading ? 'Saving...' : 'Save Student'}
          </button>
        )}
      </div>
    </form>
  );
};

export default AddStudent;





